import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import ErrorBoundary from '../components/resilience/ErrorBoundary';
import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import Layout from '../Layout';
import TXLivePage from '../pages/TXLive/TXLivePage';
import LogoutDialog from '../components/organisms/LogoutDialog/LogoutDialog';
import LineupErrorsDialog from '../components/organisms/LineupErrorsDialog/LineupErrorsDialog';
import CommandConsoleDrawer from '../components/organisms/CommandConsole/CommandConsoleDrawer';
import NoSessionScreen from '../components/organisms/NoSessionScreen/NoSessionScreen';

import { getStoredSession, initializeSession, saveSessionToStorage } from '../utils/sessionManager';
import sessionService from '../services/session.service';
import authService from '../services/auth.service';
import { checkRendererLockStatus, logClientSessionEvent } from '../services/bbSession.service';
import { hasAedriveAccess, ENFORCE_APP_ACCESS, isSuperAdmin } from '../utils/appAccess';
import { isCopilotView, canSwitchView, setViewOverride } from '../utils/viewMode';
import { isCommandLogEnabled, setCommandLogEnabled } from '../utils/commandLog';
import { buildOrgPath, resolveNamespaceRedirect } from '../utils/routePaths';
import { setFixtureFlags, getFixtureFlags } from '../services/allFixturesLive.service';
import { useFixtureFlagSync } from '../hooks/useFixtureFlagSync';
import { describeFlagChange } from '../utils/flagChangeMessage';
import { pushNotification } from '../utils/notificationLog';

// Lazy — CISPage pulls in @ae/rugby and @mui/x-charts, which every football
// operator's bundle was loading on page load for a route the vast majority
// of orgs can never reach (CIS_ORG_IDS is allowlist-gated). Code-split so
// that weight only downloads for someone who actually lands on /cis/.
const CISPage = lazy(() => import('../pages/CIS/CISPage'));

/** Route boundary keyed on pathname, so navigation clears a failed route. */
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary variant="route" resetKey={location.pathname} label="This page">
      {children}
    </ErrorBoundary>
  );
};

const ProtectedRoute = ({ children }) => {
  let session = getStoredSession();

  if (!session) {
    const urlSession = initializeSession(window.location.href);
    if (urlSession?.orgId) {
      session = urlSession;
    }
  }

  if (!session?.orgId) {
    return <Navigate to="/" replace />;
  }

  // OPUS-902: soft app-access gate (apps.aedrive.access). Logs a would-deny but
  // never blocks (preserves the live-match copilot flow) until enforced.
  if (!hasAedriveAccess(session) && ENFORCE_APP_ACCESS) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Fallback store for fixtures all_fixtures does not cover.
 *
 * NOT the source of truth — all_fixtures.is_live is. This is only consulted
 * when that store has no row for the fixture, so Drive still works on one it
 * does not know about.
 */
const LOCAL_LIVE_KEY = 'is_live_enabled';

/**
 * OPUS-1234: namespace guard. The CIS allowlist — not the URL — decides which
 * view an org gets, so a CIS org that lands on /competition/ (or a football org
 * on /cis/) is redirected to the namespace it actually belongs to. Any
 * fixture/renderer deep link is preserved across the swap, so an operator
 * opening a stale bookmark keeps their selection.
 *
 * Wraps both route trees, which is what keeps the two views from ever rendering
 * for the wrong org — neither page has to know the other exists.
 */
const NamespaceGuard = ({ children }) => {
  const { orgId, fixtureId, rendererId } = useParams();
  const { pathname } = useLocation();

  // The rule itself lives in routePaths so it can be unit-tested; this
  // component only applies the result.
  const redirectTo = resolveNamespaceRedirect({ orgId, pathname, fixtureId, rendererId });

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

const TXLiveRoute = () => {
  const { orgId, fixtureId: urlFixtureId, rendererId: urlRendererId } = useParams();
  const [session, setSession] = useState(() => {
    const stored = getStoredSession();
    // OPUS-1452: a deep link (fixture + renderer in the path) names its own
    // org — trust it over a stale stored session left over from a
    // previously-viewed competition on this device/browser, instead of
    // discarding the URL and bouncing back to whatever org was last active.
    // The bare /competition/:orgId/ route has no such link to preserve, so
    // a mismatch there still falls through to the redirect below.
    if (stored?.orgId && urlFixtureId && urlRendererId && stored.orgId !== orgId) {
      const reconciled = { ...stored, orgId };
      saveSessionToStorage(reconciled);
      return reconciled;
    }
    return stored;
  });

  // Same reconciliation as the lazy initializer above, but for when this
  // route instance is reused (React Router doesn't remount TXLiveRoute just
  // because :orgId/:fixtureId/:rendererId changed) rather than freshly
  // mounted — e.g. following a deep link to a different competition while
  // already on this route.
  useEffect(() => {
    if (session?.orgId && urlFixtureId && urlRendererId && session.orgId !== orgId) {
      const reconciled = { ...session, orgId };
      saveSessionToStorage(reconciled);
      setSession(reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, urlFixtureId, urlRendererId]);

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [fixtureOverlayOpen, setFixtureOverlayOpen] = useState(false);
  const [rendererOverlayOpen, setRendererOverlayOpen] = useState(false);
  /**
   * OPUS-1505 — Live is a property of the FIXTURE, not of this browser.
   *
   * It used to be seeded from localStorage (`is_live_enabled`). That made a
   * per-browser boolean stand in for a shared server row, and it is why a match
   * could go off air on its own: see the read effect below.
   *
   * Seeded false and immediately replaced by the server value for whichever
   * fixture is open. all_fixtures.is_live is the single source.
   */
  const [isLive, setIsLive] = useState(false);
  const [selectionDisplay, setSelectionDisplay] = useState({ fixture: '', renderer: '', aetherUrl: null });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Both LOCKED and DELETED renderers are soft now — neither blocks the route.
  // A locked renderer flows down as read-only metadata (rendererLockInfo); a
  // deleted renderer flows down as a boolean (rendererDeleted) so TXLivePage can
  // clear the stale selection and open the renderer picker instead of hard-
  // stopping the whole app.
  const [rendererDeleted, setRendererDeleted] = useState(false);
  const [rendererLockInfo, setRendererLockInfo] = useState(null);
  /**
   * OPUS-1521 — NLE is a property of the FIXTURE, not of this browser.
   *
   * It used to be seeded from localStorage ('nle_enabled_override') and default
   * TRUE. That made a per-browser boolean stand in for a shared server row, and
   * it never even reached the gate: @bb/event-service read a DIFFERENT key
   * ('is_nle_enabled') that nothing wrote, so its own `true` default always won
   * and every event published to /topic/nle regardless of this switch.
   *
   * Seeded false and immediately replaced by all_fixtures.is_nle for whichever
   * fixture is open, exactly like isLive above.
   *
   * There is deliberately NO local fallback for this one. The Live fallback
   * (LOCAL_LIVE_KEY) exists so Drive still WORKS on a fixture all_fixtures does
   * not cover; NLE off on such a fixture just means one fewer topic, which is
   * safe. NLE on with no server row would be the per-browser global again under
   * a new name.
   */
  const [nleEnabled, setNleEnabled] = useState(false);

  // Mirrors, so the sync callback can compare against the CURRENT values without
  // being rebuilt (and re-subscribing) on every toggle.
  const isLiveRef = useRef(isLive);
  const nleRef = useRef(nleEnabled);
  useEffect(() => { isLiveRef.current = isLive; }, [isLive]);
  useEffect(() => { nleRef.current = nleEnabled; }, [nleEnabled]);

  /**
   * Blunt fallback for suppressing a notification about our own write, used when the
   * session carries no userId to compare the notice's actor against. Generous
   * enough to cover the write plus the 750ms notice debounce.
   */
  const suppressSelfUntilRef = useRef(0);
  const markSelfChange = () => { suppressSelfUntilRef.current = Date.now() + 5000; };

  // Lineup validation issues bubbled up from TXLive, surfaced via the footer
  // eye icon + modal (all roles now — the drive footer reuses the copilot footer).
  const [lineupErrors, setLineupErrors] = useState([]);
  const [errorsModalOpen, setErrorsModalOpen] = useState(false);
  const [commandConsoleOpen, setCommandConsoleOpen] = useState(false);
  // OPUS-1505 — the DDS Object viewer. Not persisted: it is a diagnostic you
  // open to answer a question and close again, and a panel that reappears on
  // its own mid-match is a hazard, not a convenience. Contrast Command Log,
  // which persists because it has to be ARMED before the thing you want to
  // capture happens.
  const [ddsViewerOpen, setDdsViewerOpen] = useState(false);
  // Command Log is a diagnostic capture, off by default — it records every
  // broker/event-service command and some payloads are large (clock events
  // carry both full squads). Operators turn it on only to capture a problem.
  const [commandLogEnabled, setCommandLogEnabledState] = useState(() =>
    isCommandLogEnabled(),
  );

  // STOMP broker connection state bubbled up from TXLive, surfaced via the
  // footer's Event Service signal (idle/connecting/connected/reconnecting).
  const [brokerStatus, setBrokerStatus] = useState('idle');

  // Opened when a deep-linked fixture can't be resolved (unknown id, or a
  // competition this org isn't entitled to). Without it the operator lands on a
  // blank page with no prompt: neither overlay auto-opens when the URL carries a
  // fixture. Stable identity — TXLivePage holds it in an effect dependency list.
  const handleOpenFixtureOverlay = useCallback(() => setFixtureOverlayOpen(true), []);

  const handleLiveToggle = (nextValue) => {
    const next = typeof nextValue === 'boolean' ? nextValue : !isLive;
    // NLE depends on Live — turning Live off must not leave NLE armed with no
    // Live workflow behind it. Reflected locally so the switch greys out at
    // once; the SERVER performs the actual cascade, in the same patch, so the
    // request below deliberately carries is_live ONLY. Sending is_nle too would
    // be rejected as an unknown_field by a DWS that has not shipped OPUS-1521
    // yet — and that would break turning Live off altogether.
    if (!next) setNleEnabled(false);
    setIsLive(next);
    // Keep the fallback current for fixtures with no all_fixtures row. Harmless
    // where a row exists, because the read effect above prefers the server.
    localStorage.setItem(LOCAL_LIVE_KEY, String(next));
    // Published straight from the handler, for the fixture on screen and no
    // other. This used to go through an effect that also wrote to whichever
    // fixture this client PREVIOUSLY considered live — which is the bug being
    // removed. An operator action writes exactly the fixture the operator was
    // looking at.
    if (urlFixtureId) {
      markSelfChange();
      setFixtureFlags(urlFixtureId, { is_live: next });
    }
  };

  /**
   * Read the on-air state for the fixture on screen. READ ONLY.
   *
   * all_fixtures.is_live is the single source of truth. This effect adopts it;
   * nothing else sets isLive except an explicit operator toggle.
   *
   * WHAT THIS REPLACES, AND WHY IT MATTERED
   * ---------------------------------------
   * There used to be a second effect that published on every change of
   * "which fixture should be flagged live", derived from local state:
   *
   *     if (previous) setFixtureLive(previous, false);
   *     if (target)   setFixtureLive(target, true);
   *
   * That is how a match went off air with nobody touching it. Operator B opens
   * fixture X, the reconcile adopts is_live:true, and B's client now believes it
   * owns X. B navigates to Y — or reopens on Y — and the effect writes
   * is_live:false to X, while operator A is still running it. A's screen still
   * says Live; the row says otherwise, and Aether shows it off air.
   *
   * The handover existed to stop stale flags accumulating across a session. But
   * a client cannot infer, from its own navigation, that a fixture it is no
   * longer looking at should come off air — another operator may be on it. That
   * is a decision only an explicit toggle on that fixture can make.
   *
   * An unreadable flag is left ALONE, never assumed false: a fixture with no
   * all_fixtures row, or a failed read, must not silently take a match off air —
   * which is the very thing this exists to prevent.
   */
  useEffect(() => {
    if (!urlFixtureId) return undefined;
    let cancelled = false;
    (async () => {
      const res = await getFixtureFlags(urlFixtureId);
      if (cancelled) return;
      if (res.ok) {
        // all_fixtures has an opinion. It wins, always.
        setIsLive(res.isLive);
        // CLAMPED, not trusted. A row that somehow holds
        // { is_live:false, is_nle:true } must not put this client into a state
        // the toggles cannot express — the NLE switch is disabled while Live is
        // off, so it could never be turned back down.
        setNleEnabled(res.isLive && res.isNle);
        return;
      }
      // No row for this fixture, or the read failed — fall back to the local
      // flag so Drive still works on a fixture all_fixtures does not cover.
      // Safe now in a way it was not before: nothing here WRITES, so a stale
      // local value can only mislead this one screen. It can no longer take
      // somebody else's match off air.
      setIsLive(localStorage.getItem(LOCAL_LIVE_KEY) === 'true');
      // No row, no NLE. Deliberately no fallback — see the state seed above.
      setNleEnabled(false);
    })();
    return () => { cancelled = true; };
  }, [urlFixtureId]);

  /**
   * Adopt Live/NLE changes made elsewhere — Aether's All Fixtures grid, or
   * another Drive on the same fixture — without a reload. OPUS-1521.
   */
  useFixtureFlagSync({
    fixtureId: urlFixtureId,
    selfActorId: session?.userId ?? null,
    getSuppressUntil: () => suppressSelfUntilRef.current,
    onFlagsChanged: ({ isLive: nextLive, isNle: nextNle }, { self, actorName }) => {
      const change = describeFlagChange({
        prevLive: isLiveRef.current,
        nextLive,
        prevNle: nleRef.current,
        nextNle,
        actorName,
      });
      setIsLive(nextLive);
      // Clamped for the same reason as the read effect above.
      setNleEnabled(nextLive && nextNle);
      // Our own writes reconcile silently: the operator already knows, and
      // Aether raises its own confirmation for changes made there.
      //
      // The log is module-scoped, so unlike the old route-level toast state this
      // survives TXLive's key=-driven remount on a fixture change by construction.
      if (!self && change) pushNotification(change.message, change.severity);
    },
  });

  const handleNleToggle = (nextValue) => {
    const next = typeof nextValue === 'boolean' ? nextValue : !nleEnabled;
    // The footer already disables this control while Live is off; this is the
    // belt-and-braces half of the same rule, for a keyboard or programmatic path.
    // DWS rejects it too — three layers, because an armed NLE workflow on a
    // fixture that is not on air publishes graphics nobody is watching for.
    if (next && !isLive) return;
    setNleEnabled(next);
    if (urlFixtureId) {
      markSelfChange();
      setFixtureFlags(urlFixtureId, { is_nle: next });
    }
  };

  // Super admin only. Evaluated once per render from the JWT's permissions[]
  // claim — see isSuperAdmin in utils/appAccess. Fails closed.
  const showDdsViewer = isSuperAdmin(session);

  const handleCommandLogToggle = (nextValue) => {
    setCommandLogEnabledState((prev) => {
      const next = typeof nextValue === 'boolean' ? nextValue : !prev;
      // commandLog owns its own persistence, and clears everything it holds
      // (memory + localStorage) when switched off.
      setCommandLogEnabled(next);
      if (!next) setCommandConsoleOpen(false);
      logClientSessionEvent(getStoredSession()?.sessionId, 'command_log_toggle_changed', 'ae-drive-web', { next });
      return next;
    });
  };

  // Effective view — honours a privileged user's saved override (utils/viewMode);
  // falls back to the session's real role for everyone else.
  const isCopilot = isCopilotView(session);

  // Reflect the effective view in the browser tab title: copilot view shows
  // "Copilot", every other view keeps the default "AE Drive".
  useEffect(() => {
    document.title = isCopilot ? 'Copilot' : 'AE Drive';
  }, [isCopilot]);

  // View switcher (super user / admin / tester only). Persists the chosen view
  // and reloads so the whole app re-renders in it; toggling back restores the
  // other view. The session's real role is never changed.
  const handleViewToggle = (nextValue) => {
    const next = typeof nextValue === 'boolean' ? nextValue : !isCopilot;
    setViewOverride(next ? 'copilot' : 'drive');
    // Fire before the reload, not after — a fire-and-forget request queued
    // after this point would never actually leave the browser.
    logClientSessionEvent(getStoredSession()?.sessionId, 'view_toggle_changed', 'ae-drive-web', { next: next ? 'copilot' : 'drive' });
    window.location.reload();
  };

  // Session expiry check
  const handleSessionExpiry = useCallback(() => {
    // Clear local storage
    localStorage.clear();
    setSessionExpired(true);
    setSession(null);
  }, []);

  // Check session validity periodically
  useEffect(() => {
    const checkSession = () => {
      const currentSession = getStoredSession();

      // If session was removed or is invalid
      if (!currentSession || !currentSession.orgId) {
        handleSessionExpiry();
        return;
      }

      // Check if token is expired
      if (authService.isTokenExpired()) {
        // Short URL sessions have no refresh token — redirect back to the
        // session service so the user can re-authenticate via the login page.
        if (currentSession.isShortUrl) {
          handleSessionExpiry();
          return;
        }
        // Try to refresh the token
        authService.refreshToken().then((refreshed) => {
          if (!refreshed) {
            handleSessionExpiry();
          }
        });
      }
    };

    // Check immediately
    checkSession();

    // Check every 30 seconds
    const intervalId = setInterval(checkSession, 30000);

    return () => clearInterval(intervalId);
  }, [handleSessionExpiry]);

  // Re-validate renderer lock/existence on every mount/param change, and
  // periodically pick up a lock/unlock/delete that happens mid-session. Both
  // cases are soft — neither blocks the route; they flow down to TXLivePage as
  // metadata (rendererLockInfo) / a boolean (rendererDeleted) instead.
  useEffect(() => {
    const rendererId = urlRendererId || session?.rendererId;
    if (!rendererId) {
      setRendererDeleted(false);
      setRendererLockInfo(null);
      return;
    }

    let cancelled = false;
    const check = () => {
      checkRendererLockStatus(rendererId)
        .then((lockInfo) => {
          if (cancelled) return;
          if (lockInfo?.deleted) {
            setRendererDeleted(true);
            setRendererLockInfo(null);
          } else {
            setRendererDeleted(false);
            setRendererLockInfo(lockInfo?.locked ? lockInfo : null);
          }
        })
        .catch(() => {
          // Network/auth error checking lock status — don't block on a failed check.
        });
    };

    check();
    // Re-poll so a lock/unlock/delete made in another tab/session is picked up
    // without a page refresh. Kept short (5s, was 30s) — a longer window let a
    // user start sending broker/graphics commands for several seconds after
    // another user locked the renderer, before this client's next poll caught up.
    const intervalId = setInterval(check, 5000);

    return () => { cancelled = true; clearInterval(intervalId); };
  }, [urlRendererId, session?.rendererId]);

  // Auto-open fixture overlay on fresh session when nothing is selected
  useEffect(() => {
    if (!hasInitialized && !sessionExpired) {
      const storedFixtureId = localStorage.getItem("selectedFixtureId");
      const storedRendererSelection = localStorage.getItem("rendererSelection");
      const hasStoredRenderer = storedRendererSelection ? JSON.parse(storedRendererSelection)?.master : null;
      
      // Open fixture overlay if no fixture is selected (from URL or localStorage)
      if (!urlFixtureId && !storedFixtureId) {
        setFixtureOverlayOpen(true);
      }
      // Open renderer overlay if fixture is selected but no renderer
      else if ((urlFixtureId || storedFixtureId) && !urlRendererId && !hasStoredRenderer) {
        setRendererOverlayOpen(true);
      }
      
      setHasInitialized(true);
    }
  }, [hasInitialized, urlFixtureId, urlRendererId, sessionExpired]);

  // If session expired, show the no session screen
  if (sessionExpired || !session) {
    return <NoSessionScreen />;
  }

  if (!session?.orgId) {
    // buildOrgPath(undefined) still resolves to /competition/undefined/ here —
    // this branch fires precisely because orgId is missing, so the destination
    // is degenerate either way. Using the shared builder at least keeps this in
    // sync with every other redirect rather than duplicating the template.
    return <Navigate to={buildOrgPath(session?.orgId)} replace />;
  }

  if (session.orgId !== orgId) {
    // A deep link's org gets reconciled into `session` by the effect above
    // on the next render — don't bounce away from the URL in the meantime.
    // With no fixture/renderer in the path, there's no reconciliation coming,
    // so a mismatch here really does mean "go to your own session's org."
    if (!urlFixtureId || !urlRendererId) {
      return <Navigate to={buildOrgPath(session?.orgId)} replace />;
    }
    return null;
  }

  const handleLogout = async (removeFromDB) => {
    if (removeFromDB) {
      await sessionService.deleteSession();
    }
    window.location.href = '/';
  };

  return (
    <>
      <Layout
        showHeader={false}
        showFooter={true}
        flushTop={isCopilot}
        footerProps={{
          onLogout: () => setLogoutDialogOpen(true),
          selectionDisplay,
          onFixtureCogClick: () => setFixtureOverlayOpen(!fixtureOverlayOpen),
          onRendererCogClick: () => setRendererOverlayOpen(!rendererOverlayOpen),
          fixtureOverlayOpen,
          rendererOverlayOpen,
          toggles: [
            { label: 'Live', value: isLive, onChange: handleLiveToggle },
            {
              label: 'NLE',
              value: nleEnabled,
              onChange: handleNleToggle,
              disabled: !isLive,
              tooltip: isLive ? undefined : 'NLE needs Live enabled.'
            },
            // View switcher — only for privileged roles (super user / admin / tester).
            ...(canSwitchView(session)
              ? [{ label: 'Copilot View', value: isCopilot, onChange: handleViewToggle }]
              : []),
            { label: 'Command Log', value: commandLogEnabled, onChange: handleCommandLogToggle },
            // OPUS-1505 — DDS Object. Super admin only; spread out entirely for
            // everyone else so it is absent from the menu rather than disabled,
            // matching how Copilot View is gated above.
            ...(showDdsViewer
              ? [{ label: 'DDS Object', value: ddsViewerOpen, onChange: () => setDdsViewerOpen((v) => !v) }]
              : []),
          ],
          // Footer matches the copilot footer for ALL roles now (drive reuses it):
          // lineup-issue eye icon + modal, and compact icon statuses (signal bars +
          // battery) instead of text. lineupErrors is computed for every role in
          // TXLive, so the eye icon works for drive too.
          lineupErrors,
          onLineupErrorsClick: () => setErrorsModalOpen(true),
          // No log, no console — the terminal icon only appears once the
          // Command Log toggle above is on.
          onCommandConsoleClick: commandLogEnabled
            ? () => setCommandConsoleOpen((v) => !v)
            : undefined,
          commandConsoleOpen,
          brokerStatus,
          compactStatus: true,
          // Copilot view can't change the fixture (hide the cog, keep the read-only name).
          lockFixtureChange: isCopilot,
          pullInfo: session?.username ? {
            initials: session.username.split(/\s+/).map((n) => n[0] || '').join('').slice(0, 2).toUpperCase() || 'U',
            text: session.username,
          } : null,
        }}
      >
        <TXLivePage
          fixtureOverlayOpen={fixtureOverlayOpen}
          rendererOverlayOpen={rendererOverlayOpen}
          ddsViewerOpen={showDdsViewer && ddsViewerOpen}
          onCloseDdsViewer={() => setDdsViewerOpen(false)}
          isLive={isLive}
          isNleEnabled={nleEnabled}
          onCloseFixtureOverlay={() => setFixtureOverlayOpen(false)}
          onCloseRendererOverlay={() => setRendererOverlayOpen(false)}
          onOpenFixtureOverlay={handleOpenFixtureOverlay}
          onOpenRendererOverlay={() => setRendererOverlayOpen(true)}
          onSelectionDisplayChange={setSelectionDisplay}
          urlFixtureId={urlFixtureId}
          urlRendererId={urlRendererId}
          rendererLockInfo={rendererLockInfo}
          rendererDeleted={rendererDeleted}
          onLineupErrorsChange={setLineupErrors}
          onBrokerStatusChange={setBrokerStatus}
        />
      </Layout>
      <LineupErrorsDialog
        open={errorsModalOpen}
        onClose={() => setErrorsModalOpen(false)}
        errors={lineupErrors}
      />
      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onLogout={handleLogout}
      />
      {commandLogEnabled && (
        <CommandConsoleDrawer
          open={commandConsoleOpen}
          onClose={() => setCommandConsoleOpen(false)}
        />
      )}
    </>
  );
};

/**
 * CIS (rugby) route — OPUS-1234.
 *
 * Gated behind <ProtectedRoute> (a session must exist) — CISPage reads real
 * fixture/team/player_stat/team_stat off DDS with no token of its own
 * (ddsClient.js sends none), so an open route serves live squad and stat data
 * to anyone with the URL. That was an acceptable trade-off while this only
 * rendered static demo data; it stopped being one once the mock-mode escape
 * hatch was removed and every request here started reaching real data.
 *
 * Deliberately NOT wrapped in <NamespaceGuard> for the two fixtureId-only
 * routes below (`/cis`, `/cis/fixture/:fixtureId`) — they carry no `:orgId`,
 * so NamespaceGuard's `useParams().orgId` would read undefined and bounce an
 * authenticated football operator who opens one straight to
 * `/competition/undefined/`. The org-scoped `/cis/:orgId/` route further down
 * uses both, exactly like the `/competition/:orgId` tree.
 */
const CISRoute = () => {
  const { fixtureId: urlFixtureId } = useParams();

  useEffect(() => {
    document.title = 'CIS';
  }, []);

  return (
    <Layout showHeader={false} showFooter={false} flushTop>
      <Suspense fallback={<div style={{ padding: 32, textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>Loading…</div>}>
        <CISPage fixtureId={urlFixtureId} />
      </Suspense>
    </Layout>
  );
};

const AppRoutes = () => {
  const session = getStoredSession();
  
  // If no session, try to initialize from URL one more time
  if (!session) {
    const urlSession = initializeSession(window.location.href);
    if (urlSession?.orgId) {
      // Session found in URL — land on whichever namespace this org belongs to.
      return <Navigate to={buildOrgPath(urlSession.orgId)} replace />;
    }
  }

  return (
    // Route-tier boundary, keyed on pathname so navigating away resets it — a
    // broken route must not stay broken, and must not take the rest of the app
    // with it.
    <RouteErrorBoundary>
    <Routes>
      {/* Football (drive + copilot) — the original namespace. */}
      <Route path="/competition/:orgId/" element={<ProtectedRoute><NamespaceGuard><TXLiveRoute /></NamespaceGuard></ProtectedRoute>} />
      <Route path="/competition/:orgId/fixture/:fixtureId/renderer/:rendererId/" element={<ProtectedRoute><NamespaceGuard><TXLiveRoute /></NamespaceGuard></ProtectedRoute>} />

      {/* CIS / rugby (OPUS-1234) — its own page, deliberately NOT routed through
          TXLiveRoute: TXLive is a 3k-line football-specific view and branching
          it a third way is what this work exists to avoid. CIS is read-only
          for commentators, not an operator console — there is no renderer to
          lock/select, so `fixtureId` alone drives it; `fixtureId` is optional
          too, so a bare /cis renders the same page.

          Org-scoped, mirroring /competition/:orgId (both ProtectedRoute AND
          NamespaceGuard) — this is what makes getNamespaceForOrg('cis')
          resolve to something once an org is added to config/cisOrgs.js's
          CIS_ORG_IDS, instead of a URL nothing here matches (e.g. the plain
          `/` redirect once session?.orgId is CIS-allowlisted).

          No .../fixture/:fixtureId/renderer/:rendererId variant: nothing in
          CIS ever selects a renderer (there is none), so no CIS-side flow
          constructs that URL shape. The one FOOTBALL-side caller that could —
          SessionContext.js's short-URL resolver, for an org's OLD grandfathered
          link from before it was migrated to CIS — is accepted as a 404-ish
          bounce to the catch-all, not worth a route only that stale-link case
          would ever hit.

          The org+fixture variant below DOES matter, though: it is where
          buildOrgPath/resolveNamespaceRedirect land a CIS deep link (see
          routePaths.js). Without it, /cis/:orgId/ on its own has nothing to
          show (CISRoute reads only fixtureId, which the bare org route never
          supplies) — a real dead end for any redirect that swapped an org
          into this namespace mid-visit. */}
      <Route path="/cis/:orgId/" element={<ProtectedRoute><NamespaceGuard><CISRoute /></NamespaceGuard></ProtectedRoute>} />
      <Route path="/cis/:orgId/fixture/:fixtureId/" element={<ProtectedRoute><NamespaceGuard><CISRoute /></NamespaceGuard></ProtectedRoute>} />

      {/* fixtureId-only, no :orgId — kept for links that don't carry a session's
          org (e.g. Aether's "Launch in CIS" button). ProtectedRoute only, per
          the NamespaceGuard note in the doc comment above. */}
      <Route path="/cis" element={<ProtectedRoute><CISRoute /></ProtectedRoute>} />
      <Route path="/cis/fixture/:fixtureId" element={<ProtectedRoute><CISRoute /></ProtectedRoute>} />

      <Route
        path="/"
        element={
          session?.orgId ? (
            <Navigate to={buildOrgPath(session.orgId)} replace />
          ) : (
            <NoSessionScreen />
          )
        }
      />
      <Route
        path="*"
        element={
          session?.orgId ? (
            <Navigate to={buildOrgPath(session.orgId)} replace />
          ) : (
            <NoSessionScreen />
          )
        }
      />
    </Routes>
    </RouteErrorBoundary>
  );
};

export default AppRoutes;
